// ============================================================================
// Prime is the lead (2026-10-10, the roundup's "Lead is Prime"): every typed
// "Ask Prime" (the Ask bar and sheet, the Home widget, the Add sheet's AI mode,
// Search, a waiting capture's "Talk to Prime", Today's ask icon) goes to Prime
// on Claude Code, the AgentRun with template `prime`. The in-app brain is the
// "Quick chat" and is reached only by voice, the desktop page panel and the
// Agents list. Pure: the hook that sends is useSendToPrime in os/AskPrime.tsx.
// ============================================================================
import type { AgentRunResource } from '@/kinds/agentrun/apply'
import { isOver } from '@/kinds/agentrun/schema'
import { plainWords } from './plainWords'

/** The name the in-app brain's thread goes by wherever it still shows. */
export const QUICK_CHAT = 'Quick chat'

/** Prime's session among the runs: the one that is not over (Suspended is live: a message wakes it). */
export const liveLead = (runs: readonly AgentRunResource[] | undefined): AgentRunResource | undefined =>
  (runs ?? []).find((r) => r.spec.template === 'prime' && !r.deleted_at && !isOver(r.spec.status))

/**
 * What the Add sheet's AI mode says to Prime: the owner's words, framed as
 * something to file, naming the workspace the sheet was in (Prime sees them all).
 */
export const fileThis = (text: string, workspace?: string): string =>
  `File this${workspace ? ` in the ${workspace} workspace` : ''}: ${text.trim()}`

/**
 * The newest thing Prime said in its thread, for the Home widget and the Ask
 * sheet: plain words (lib/plainWords.ts), since both clip it to a line or two
 * and markdown there is noise (a diagram's source, stars, fences).
 */
export const leadLast = (run: AgentRunResource | undefined): string | undefined => {
  const msgs = run?.spec.messages ?? []
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i]!
    const words = m.role === 'assistant' ? plainWords(m.text) : ''
    if (words) return words
  }
  return undefined
}

/** Words to Prime that have not reached the factory yet (a picture sent now would overtake them). */
export const sayPending = (run: AgentRunResource): boolean =>
  (run.status.writebacks ?? []).some((w) => w.action === 'say')
